import { useEffect, useRef } from "react";
import L, { type Map as LeafletMap, type Marker } from "leaflet";
import type { Location } from "../lib/types";

interface Props {
  locations: Location[];
  selected: Location | null;
  setSelected: (loc: Location | null) => void;
}

/**
 * Leaflet wrapper. Recreates markers when `locations` changes, recenters when
 * `selected` changes. Uses CartoDB Voyager tiles to avoid OSM rate limits.
 */
export function MapView({ locations, selected, setSelected }: Props) {
  const mapRef = useRef<LeafletMap | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<Record<string, Marker>>({});

  // create the map exactly once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      scrollWheelZoom: true,
      touchZoom: true,
      worldCopyJump: true,
      minZoom: 2,
      maxZoom: 19,
    }).setView([39.5, -40], 3);
    L.control.zoom({ position: "topright" }).addTo(map);
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      {
        attribution: "© OpenStreetMap, © CARTO",
        subdomains: "abcd",
        maxZoom: 19,
      }
    ).addTo(map);
    mapRef.current = map;

    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // re-render markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};

    const makeIcon = (loc: Location, isSelected: boolean) => {
      const color = isSelected ? "#1A1A1A" : "#DA291C";
      return L.divIcon({
        className: "mg-pin-wrap",
        html: `
          <div class="mg-pin ${isSelected ? "selected" : ""}">
            <svg viewBox="0 0 24 32">
              <path d="M12 0 C5 0 0 5 0 12 C0 22 12 32 12 32 C12 32 24 22 24 12 C24 5 19 0 12 0 Z"
                fill="${color}" stroke="white" stroke-width="1.5"/>
              <circle cx="12" cy="12" r="4.5" fill="white"/>
            </svg>
          </div>`,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -32],
      });
    };

    const bounds: [number, number][] = [];
    locations.forEach((loc) => {
      const isSelected = selected?.location_id === loc.location_id;
      const addressLine = [loc.address.city, loc.address.country]
        .filter((value) => value && value.trim())
        .join(", ");
      const tooltip = addressLine
        ? `<b>${loc.name}</b><br/>${addressLine}`
        : `<b>${loc.name}</b>`;
      const m = L.marker([loc.geo.lat, loc.geo.lng], {
        icon: makeIcon(loc, isSelected),
      })
        .addTo(map)
        .bindTooltip(tooltip, { direction: "top", offset: [0, -28] })
        .on("click", () => setSelected(loc));
      markersRef.current[loc.location_id] = m;
      bounds.push([loc.geo.lat, loc.geo.lng]);
    });

    if (selected) {
      map.setView(
        [selected.geo.lat, selected.geo.lng],
        Math.max(map.getZoom(), 10),
        { animate: true }
      );
    } else if (bounds.length) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [locations, selected, setSelected]);

  return <div ref={containerRef} className="h-full w-full" />;
}
